/* Copyright (c) 2020-present CloudIO Inc */

import { Box } from '@mui/material';
import zIndex from 'constants/ZIndex';
import useConstant from 'hooks/useConstant';
import useLocalState from 'hooks/useLocalState';
import { useBorder } from 'hooks/useMarginPadding';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  createEditor,
  Descendant,
  Transforms,
  Node as SlateNode,
  Element as SlateElement,
  Text as SlateText,
  Range as SlateRange,
  Editor,
} from 'slate';
import { withHistory } from 'slate-history';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react';
import { useTheme } from 'theme/Theme';
import { HoveringAction, HoveringToolbar } from './HoveringToolbar';

import { SlateHTMLElement, SlateLeaf, withHtml } from './HTML';
import {
  BLOCK_HOTKEYS,
  CustomElement,
  LIST_TYPES,
  MARK_HOTKEYS,
  MentionElement,
  Option,
} from './SlateTypes';
import {
  hasEmptyChildren,
  SlateProvider,
  toggleBlock,
  toggleMark,
  withLists,
} from './SlateUtils';
import { Border } from 'componentregistry';
import imageExtensions from 'image-extensions';
import isUrl from 'is-url';

export const EMPTY_VALUE = Object.freeze([
  {
    type: 'p',
    children: [{ text: '' }],
  },
]) as Descendant[];

export const Portal = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactPortal | null => {
  return ReactDOM.createPortal(children, document.body);
};

const insertMention = (editor: Editor, option: Option) => {
  const mention: MentionElement = {
    type: 'mention',
    option,
    children: [{ text: '' }],
  };
  Transforms.insertNodes(editor, mention);
  Transforms.move(editor);
};

const withMentions = (editor: Editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element: SlateElement) => {
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = (element: SlateElement) => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  return editor;
};

const insertImage = (editor: Editor, url: string) => {
  const text = { text: ' ' };
  const img: CustomElement = { type: 'img', url, children: [text] };
  Transforms.insertNodes(editor, img);
  Transforms.insertNodes(editor, {
    type: 'p',
    children: [{ color: '', text: ' ' }],
  });
};
const isImageUrl = (url: string) => {
  if (!url) return false;
  if (!isUrl(url)) return false;
  const ext = new URL(url).pathname.split('.').pop();
  return ext && imageExtensions.includes(ext);
};
const withImages = (
  editor: Editor,
  onImageUpload?: (file: File) => Promise<string>,
) => {
  const { insertData, isVoid } = editor;
  editor.isVoid = (element) => {
    return element.type === 'img' ? true : isVoid(element);
  };
  editor.insertData = (data) => {
    const text = data.getData('text/plain');
    const { files } = data;
    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader();
        const [mime] = file.type.split('/');
        if (mime === 'image') {
          if (onImageUpload) {
            onImageUpload(file)
              .then((url) => {
                if (url) {
                  insertImage(editor, url);
                }
              })
              .catch((error) => {
                console.error('Failed to upload image:', error);
              });
          } else {
            // Fallback to base64 conversion if no onImageUpload provided
            reader.addEventListener('load', () => {
              const url = reader.result as string;
              insertImage(editor, url);
            });
            reader.readAsDataURL(file);
          }
        }
      }
    } else if (isImageUrl(text)) {
      insertImage(editor, text);
    } else {
      insertData(data);
    }
  };
  return editor;
};

const SlateRTE = (
  {
    id,
    autoFocus = true,
    border,
    className,
    height = 200,
    onBlur,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    options = [],
    placeholder,
    readOnly,
    textOnly = false,
    value = EMPTY_VALUE,
    width,
    staticToolbar,
    onImageUpload,
  }: {
    autoFocus?: boolean;
    border?: Border;
    className?: string;
    height: number | string;
    onBlur?: (e: React.SyntheticEvent<HTMLDivElement>) => void;
    onChange: (val: Descendant[]) => void;
    onClick?: (e: React.SyntheticEvent<HTMLDivElement>) => void;
    onFocus?: (e: React.SyntheticEvent<HTMLDivElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => boolean | undefined;
    options?: Option[];
    placeholder?: string;
    readOnly?: boolean;
    textOnly?: boolean;
    value?: Descendant[];
    width?: number | string;
    id?: string;
    staticToolbar?: boolean;
    onImageUpload?: (file: File) => Promise<string>;
  },
  ref: React.Ref<HTMLDivElement>,
) => {
  const renderElement = useCallback(
    (props: RenderElementProps) => <SlateHTMLElement {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <SlateLeaf {...props} />,
    [],
  );
  const editor = useConstant(() =>
    withMentions(
      withLists(
        withHtml(
          withImages(
            withHistory(withReact(createEditor() as ReactEditor)),
            onImageUpload,
          ),
        ),
      ),
    ),
  );
  const localValue = Array.isArray(value) && value.length ? value : EMPTY_VALUE;
  const [val, setVal] = useLocalState(localValue, onChange);
  const theme = useTheme();
  const [target, setTarget] = useState<SlateRange | undefined>();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const mref = useRef<HTMLDivElement>(null);
  const searchLC = search.toLowerCase();
  const filteredOptions = useMemo(
    () =>
      options
        .filter((o) => (o.label || o.value).toLowerCase().startsWith(searchLC))
        .slice(0, 10),
    [options, searchLC],
  );
  const styles = useBorder(border);

  useEffect(() => {
    const el = mref.current;
    if (target && filteredOptions.length > 0 && el) {
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      let top = 0;
      if (
        rect.top + window.pageYOffset + 24 + el.scrollHeight <
        window.innerHeight
      ) {
        top = rect.top + window.pageYOffset + 24;
      } else if (rect.top + window.pageYOffset - 8 - el.scrollHeight > 0) {
        top = rect.top + window.pageYOffset - 8 - el.scrollHeight;
      }
      el.style.top = `${top}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [editor, filteredOptions.length, index, search, target]);

  useEffect(() => {
    if (val === EMPTY_VALUE && Array.isArray(value) && value.length) {
      setVal(value);
    }
    if (editor.children !== val) {
      setTarget(undefined);
      ReactEditor.deselect(editor);
      editor.children = val;
    }
  }, [editor, setVal, val, value]);

  return (
    <Slate
      editor={editor}
      initialValue={val}
      onChange={(_value) => {
        if (value !== _value) {
          setVal(_value);

          if (options.length) {
            const { selection } = editor;
            if (selection && SlateRange.isCollapsed(selection)) {
              const [start] = SlateRange.edges(selection);
              const wordBefore = Editor.before(editor, start, {
                unit: 'word',
              });
              let beforeRange =
                wordBefore && Editor.range(editor, wordBefore, start);
              let beforeText =
                beforeRange && Editor.string(editor, beforeRange);
              if (
                beforeText &&
                (beforeText === '@' || beforeText.endsWith(' @'))
              ) {
                beforeRange = Editor.range(
                  editor,
                  { ...start, offset: start.offset - 1 },
                  start,
                );
                setTarget(beforeRange);
                setSearch('');
                setIndex(0);
                return;
              }

              const before = wordBefore && Editor.before(editor, wordBefore);
              beforeRange = before && Editor.range(editor, before, start);
              beforeText = beforeRange && Editor.string(editor, beforeRange);
              const beforeMatch = beforeText?.match(/^@(\w+)$/);
              const after = Editor.after(editor, start);
              const afterRange = Editor.range(editor, start, after);
              const afterText = Editor.string(editor, afterRange);
              const afterMatch = afterText.match(/^(\s|$)/);

              if (beforeMatch && afterMatch) {
                setTarget(beforeRange);
                setSearch(beforeMatch[1]);
                setIndex(0);
                return;
              }
            }
            setTarget(undefined);
          }
        }
      }}
    >
      <SlateProvider>
        {textOnly ? null : <HoveringToolbar staticToolbar={staticToolbar} />}
        <Box
          className={className}
          ref={ref}
          sx={{
            border: styles.border,
            bgcolor: 'background.paper',
            padding: textOnly || staticToolbar ? '0px' : '0 48px',
            display: 'flex',
            flexDirection: 'column',
            width: width ?? '100%',
            height,
            position: 'relative',
            overflow: 'auto',
            '& p': textOnly ? { margin: 0, padding: 0 } : {},
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.divider}`,
              paddingLeft: '40px',
              marginLeft: '0px',
              color: theme.palette.text.secondary,
              '& p': {
                margin: 0,
                padding: 0,
              },
            },
          }}
        >
          <Editable
            id={id}
            onFocus={onFocus}
            className="slate-editor"
            readOnly={readOnly}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            spellCheck
            autoFocus={autoFocus}
            style={{
              width: 'auto',
              height: 'auto',
              minHeight: 60,
              outlineWidth: '0px',
            }}
            onBlur={(e) => {
              setTarget(undefined);
              onBlur?.(e);
            }}
            onWheel={() => {
              setTarget(undefined);
            }}
            onClick={(e) => {
              setTarget(undefined);
              onClick?.(e);
            }}
            onKeyDown={(event) => {
              if (target) {
                switch (event.key) {
                  case 'ArrowDown': {
                    event.preventDefault();
                    const prevIndex =
                      index >= filteredOptions.length - 1 ? 0 : index + 1;
                    setIndex(prevIndex);
                    break;
                  }
                  case 'ArrowUp': {
                    event.preventDefault();
                    const nextIndex =
                      index <= 0 ? filteredOptions.length - 1 : index - 1;
                    setIndex(nextIndex);
                    break;
                  }
                  case 'Tab':
                  case 'Enter':
                    event.preventDefault();
                    Transforms.select(editor, target);
                    insertMention(editor, filteredOptions[index]);
                    setTarget(undefined);
                    break;
                  case 'Escape':
                    event.preventDefault();
                    setTarget(undefined);
                    break;
                  default:
                }
              } else if (onKeyDown) {
                if (onKeyDown(event) === false) {
                  return;
                }
              }

              if ((event.ctrlKey || event.metaKey) && event.key.length === 1) {
                for (let i = 0; i < MARK_HOTKEYS.length; i++) {
                  if (MARK_HOTKEYS[i][0](event)) {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleMark(editor, MARK_HOTKEYS[i][1]);
                    break;
                  }
                }
                for (let i = 0; i < BLOCK_HOTKEYS.length; i++) {
                  if (BLOCK_HOTKEYS[i][0](event)) {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleBlock(editor, BLOCK_HOTKEYS[i][1]);
                    break;
                  }
                }
              }
              const { selection } = editor;
              if (event.key === 'Backspace' && selection) {
                const path = selection.anchor.path.slice(0, -1);
                const selectedElement = SlateNode.descendant(editor, path);
                const parent = SlateNode.parent(editor, path);
                if (
                  SlateElement.isElement(selectedElement) &&
                  SlateElement.isElement(parent)
                ) {
                  if (
                    ((selectedElement.type === 'p' &&
                      parent.type === 'quote') ||
                      (selectedElement.type === 'li' &&
                        LIST_TYPES.includes(parent.type))) &&
                    selection.focus.offset === 0 &&
                    selection.anchor.offset === 0 &&
                    SlateRange.isCollapsed(selection)
                  ) {
                    if ((parent.children[0] as unknown) === selectedElement) {
                      // cursor is at the start of the first child
                      // remove the wrapper quote or list
                      Transforms.liftNodes(editor);
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                  }
                }
              }

              if (event.key === 'Enter' && editor.selection) {
                const path = editor.selection.anchor.path.slice(0, -1);
                const selectedElement = SlateNode.descendant(editor, path);
                const parent = SlateNode.parent(editor, path);

                if (SlateElement.isElement(selectedElement)) {
                  const type = selectedElement.type;
                  if (editor.isVoid(selectedElement)) {
                    event.preventDefault();
                    event.stopPropagation();
                    Transforms.insertNodes(editor, {
                      type: 'p',
                      children: [{ text: '' }],
                    });
                    return;
                  }
                  if (
                    SlateElement.isElement(parent) &&
                    hasEmptyChildren(selectedElement)
                  ) {
                    if (
                      (type === 'li' && LIST_TYPES.includes(parent.type)) ||
                      (type === 'p' && parent.type === 'quote')
                    ) {
                      Transforms.liftNodes(editor);
                      Transforms.setNodes(editor, { type: 'p' });
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                  }
                  if (
                    [
                      'h1',
                      'h2',
                      'h3',
                      'info',
                      'warning',
                      'error',
                      'success',
                      'p',
                    ].includes(type)
                  ) {
                    event.preventDefault();
                    const selectedLeaf = SlateNode.descendant(
                      editor,
                      editor.selection.anchor.path,
                    );
                    if (event.shiftKey) {
                      Transforms.insertText(editor, '\n');
                    } else {
                      if (
                        SlateText.isText(selectedLeaf) &&
                        selectedLeaf.text.length ===
                          editor.selection.anchor.offset
                      ) {
                        Transforms.insertNodes(editor, {
                          type: 'p',
                          children: [{ text: '' }],
                        });
                      } else {
                        Transforms.splitNodes(editor);
                        Transforms.setNodes(editor, { type: 'p' });
                      }
                    }
                  }
                }
              }
            }}
          />
          {textOnly || staticToolbar ? null : <HoveringAction />}
        </Box>
      </SlateProvider>
      {target && filteredOptions.length > 0 && (
        <Portal>
          <Box
            ref={mref}
            sx={{
              top: '-9999px',
              left: '-9999px',
              position: 'absolute',
              zIndex: zIndex.AutoCompletePopper,
              padding: '4px',
              background: theme.palette.background.paper,
              borderRadius: '4px',
              boxShadow: `0 1px 5px ${theme.palette.divider}`,
            }}
            data-cy="mentions-portal"
          >
            {filteredOptions.map((option, i) => (
              <Box
                key={option.value}
                sx={{
                  padding: '2px 4px',
                  borderRadius: '4px',
                  background:
                    i === index ? theme.palette.action.selected : 'transparent',
                  cursor: 'pointer',
                  '&:hover': {
                    background: theme.palette.action.hover,
                  },
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  Transforms.select(editor, target);
                  insertMention(editor, filteredOptions[i]);
                  setTarget(undefined);
                }}
              >
                {option.label || option.value}
              </Box>
            ))}
          </Box>
        </Portal>
      )}
    </Slate>
  );
};

export default React.forwardRef(SlateRTE);
